import React from 'react';
import { resolveRiskKey, resolveRiskLabel, clampScore, RISK_BANDS } from '../../lib/riskBands';
import { confidenceDisplayLabel } from '../../lib/turnoverDisplay';
import {
  freshnessBadgeLabel,
  freshnessBadgeTitle,
  shouldBlockStaleScoreAsCurrent,
} from '../../lib/riskFreshnessDisplay';

function FreshnessBadge({ freshnessStatus, compact = false }) {
  const label = freshnessBadgeLabel(freshnessStatus);
  if (!label) return null;
  const s = String(freshnessStatus || '').toLowerCase();
  const palette =
    s === 'fresh'
      ? { color: 'var(--success-fg, #0d7a4a)', bg: 'var(--success-bg, rgba(13,122,74,0.1))' }
      : s === 'stale'
        ? { color: 'var(--warning-fg, #9a6700)', bg: 'var(--warning-bg, rgba(154,103,0,0.12))' }
        : s === 'updating'
          ? { color: 'var(--text-muted)', bg: 'var(--bg-s1)' }
          : { color: 'var(--danger-fg, #b42318)', bg: 'var(--danger-bg, rgba(180,35,24,0.1))' };
  return (
    <span
      className={`inline-flex items-center font-medium uppercase tracking-wide rounded ${
        compact ? 'text-[9px] px-1 py-px' : 'text-[10px] px-1.5 py-0.5'
      }`}
      style={{ color: palette.color, background: palette.bg }}
      title={freshnessBadgeTitle(freshnessStatus) || undefined}
    >
      {label}
    </span>
  );
}

const KEY_TO_TOKEN = {
  LOW: 'low',
  MED: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

/**
 * Numeric 0–100 risk with band label and micro band-bar.
 *
 * Variants:
 *   'inline'   — compact number + label
 *   'stacked'  — big number, label below
 *   'hero'     — huge number, label + bar (profile hero)
 *   'cell'     — table cell (tiny number + bar)
 *
 * When `ready` is false or `score` is not finite, shows blocked copy from API when provided.
 */
export default function RiskScore({
  score,
  level,
  variant = 'inline',
  showBar = true,
  className = '',
  /** When false, never show a numeric score or risk bar (even if level is set). */
  ready = true,
  confidenceLevel = null,
  confidenceNote = null,
  blockedMessage = null,
  processingState = null,
  freshnessStatus = null,
}) {
  const n =
    score != null && score !== '' && Number.isFinite(Number(score)) ? clampScore(score) : null;
  const isStaleScore = shouldBlockStaleScoreAsCurrent(freshnessStatus);
  const numericReady = ready !== false && n != null && !isStaleScore;
  const key = numericReady ? resolveRiskKey({ score: n, level }) : null;
  const label = numericReady ? resolveRiskLabel({ score: n, level }) : null;
  const token = key ? KEY_TO_TOKEN[key] || 'low' : 'low';
  const fg = `var(--risk-${token}-fg)`;
  const color = `var(--risk-${token})`;

  const showConfidence = String(confidenceLevel || '').toLowerCase() !== 'low';
  const confLabel = showConfidence ? confidenceDisplayLabel(confidenceLevel) : null;
  const blocked =
    blockedMessage && String(blockedMessage).trim()
      ? String(blockedMessage).trim()
      : null;
  const state = String(processingState || '').toLowerCase();
  const isProcessing = state === 'processing' || state === 'queued' || state === 'retrying';
  const isFailed = state === 'failed';
  const pendingTitle = isFailed
    ? 'Risk score failed'
    : isProcessing
      ? (state === 'queued' ? 'Queued…' : state === 'retrying' ? 'Retrying…' : 'Calculating…')
      : 'Risk score unavailable';
  const pendingBody = blocked
    || (isFailed
      ? 'Risk analysis could not complete. Try refreshing risk scores from Imports or Settings.'
      : isProcessing
        ? 'The risk score will appear once profile and market signals finish processing.'
        : 'Additional profile data is needed before a risk score can be shown.');

  if (isStaleScore && n != null) {
    const mutedScore = { color: 'var(--text-muted)' };
    const staleNote = blocked || 'Score is older than 24 hours. A refresh is scheduled.';
    if (variant === 'cell') {
      return (
        <div className={`flex flex-col items-end gap-0.5 shrink-0 min-w-0 max-w-[6.5rem] ${className}`}>
          <div className="flex items-center gap-1 min-w-0 justify-end flex-wrap">
            <span className="app-numeric font-semibold text-[13px] opacity-70" style={mutedScore}>
              {n}
            </span>
            <FreshnessBadge freshnessStatus="stale" compact />
          </div>
          <span className="text-[9px] text-right leading-tight" style={{ color: 'var(--text-subtle)' }} title={staleNote}>
            Not current
          </span>
        </div>
      );
    }
    if (variant === 'hero' || variant === 'stacked') {
      return (
        <div className={`flex flex-col gap-2 ${className}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="app-numeric font-semibold text-[28px] opacity-70" style={mutedScore}>
              {n}
            </span>
            <FreshnessBadge freshnessStatus="stale" />
          </div>
          <p className="text-[12px] max-w-md" style={{ color: 'var(--text-subtle)' }}>
            {staleNote}
          </p>
        </div>
      );
    }
    return (
      <span className={`inline-flex items-center gap-2 ${className}`}>
        <span className="app-numeric text-[12.5px] opacity-70" style={mutedScore}>
          {n}
        </span>
        <FreshnessBadge freshnessStatus="stale" compact />
      </span>
    );
  }

  if (!numericReady) {
    const muted = { color: 'var(--text-subtle)' };
    const freshnessBadge =
      freshnessStatus && freshnessStatus !== 'stale' ? (
        <FreshnessBadge freshnessStatus={freshnessStatus} compact />
      ) : null;
    if (variant === 'hero') {
      return (
        <div className={`flex flex-col gap-3 ${className}`}>
          <div className="flex items-end gap-3 flex-wrap">
            <span
              className="font-semibold tracking-tight text-[22px] sm:text-[26px]"
              style={{ ...muted, color: 'var(--text-muted)' }}
            >
              {pendingTitle}
            </span>
            {freshnessBadge}
          </div>
          <p className="text-[12.5px] leading-snug max-w-md" style={muted}>
            {pendingBody}
          </p>
        </div>
      );
    }
    if (variant === 'stacked') {
      return (
        <div className={`flex flex-col items-start gap-1 ${className}`}>
          <span className="text-[13px] font-medium" style={muted}>
            {pendingTitle}
          </span>
        </div>
      );
    }
    if (variant === 'cell') {
      return (
        <div className={`flex flex-col items-end gap-0.5 shrink-0 max-w-[5.75rem] min-w-0 ${className}`}>
          <span
            className="text-[10px] font-medium text-right leading-tight max-w-full truncate"
            style={muted}
            title={pendingTitle}
          >
            {pendingTitle}
          </span>
        </div>
      );
    }
    return (
      <span className={`inline-flex items-center gap-2 ${className}`}>
        <span className="text-[12.5px] font-medium" style={muted}>
          {pendingTitle}
        </span>
      </span>
    );
  }

  const s = n;
  const pct = s;

  const confidenceBadge = confLabel ? (
    <span
      className="text-[10.5px] font-medium"
      style={{ color: 'var(--text-muted)' }}
      title={confidenceNote || undefined}
    >
      {confLabel}
    </span>
  ) : null;

  if (variant === 'hero') {
    return (
      <div className={`flex flex-col gap-3 ${className}`}>
        <div className="flex items-end gap-3">
          <span
            className="app-numeric app-risk-score-hero__number"
            style={{ color: 'var(--text-primary)', fontSize: 72, lineHeight: 0.95 }}
          >
            {s}
          </span>
          <span
            className="text-[12px] mb-2"
            style={{ color: 'var(--text-subtle)' }}
          >
            / 100
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide"
              style={{
                color: fg,
                background: `var(--risk-${token}-bg)`,
                border: `1px solid var(--risk-${token}-border)`,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
              {label}
            </span>
            {confidenceBadge}
            <FreshnessBadge freshnessStatus={freshnessStatus} />
          </div>
          {showConfidence && confidenceNote && (
            <p className="text-[12px] leading-snug max-w-md" style={{ color: 'var(--text-subtle)' }}>
              {confidenceNote}
            </p>
          )}
        </div>
        {showBar && key && <BandBar activeKey={key} score={s} />}
      </div>
    );
  }

  if (variant === 'stacked') {
    return (
      <div className={`flex flex-col items-start gap-1 ${className}`}>
        <span
          className="app-numeric font-semibold tracking-tight text-[28px] leading-none"
          style={{ color: 'var(--text-primary)' }}
        >
          {s}
        </span>
        <span className="text-[11px]" style={{ color: fg }}>
          {label}
        </span>
        {confidenceBadge}
      </div>
    );
  }

  if (variant === 'cell') {
    return (
      <div className={`flex flex-col items-end gap-0.5 shrink-0 min-w-0 max-w-[5.75rem] ${className}`}>
        <div className="flex items-center gap-1.5 min-w-0 max-w-full justify-end">
          <span
            className="app-numeric font-semibold text-[13px] w-7 text-right shrink-0"
            style={{ color: 'var(--text-primary)' }}
          >
            {s}
          </span>
          {showBar && (
            <div
              className="h-[3px] w-10 rounded-full overflow-hidden shrink-0"
              style={{ background: 'var(--bg-s1)' }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
          )}
        </div>
        <span
          className="text-[10px] font-semibold uppercase tracking-wide truncate max-w-full text-right"
          style={{ color: fg }}
          title={label}
        >
          {label}
        </span>
        {confidenceBadge}
      </div>
    );
  }

  return (
    <span className={`inline-flex flex-col items-start gap-0.5 ${className}`}>
      <span className="inline-flex items-center gap-2">
        <span
          className="app-numeric font-semibold text-[14px]"
          style={{ color: 'var(--text-primary)' }}
        >
          {s}
        </span>
        <span
          className="text-[10.5px] font-semibold uppercase tracking-wide"
          style={{ color: fg }}
        >
          {label}
        </span>
      </span>
      {confidenceBadge}
    </span>
  );
}

function BandBar({ activeKey, score }) {
  const markerLeft = Number.isFinite(score) ? `${score}%` : null;

  return (
    <div className="w-full app-risk-score-hero__bar">
      <div className="relative w-full" style={{ height: 10 }}>
        <div
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex w-full gap-[2px] rounded overflow-hidden"
          style={{ height: 6 }}
        >
          {RISK_BANDS.map((b) => {
            const isActive = b.key === activeKey;
            const widthPct = ((b.max - b.min + 1) / 101) * 100;
            const bandToken = KEY_TO_TOKEN[b.key];
            return (
              <div
                key={b.key}
                style={{
                  width: `${widthPct}%`,
                  background: isActive ? `var(--risk-${bandToken})` : 'var(--bg-s2)',
                  opacity: isActive ? 1 : 0.6,
                }}
              />
            );
          })}
        </div>
        {markerLeft != null && (
          <span
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: markerLeft,
              width: 10,
              height: 10,
              background: 'var(--text-primary)',
              border: '2px solid var(--bg-card)',
              boxShadow: '0 0 0 1px var(--border-divider)',
            }}
            aria-hidden
          />
        )}
      </div>
      <div className="relative mt-1.5 w-full text-[10px] app-numeric" style={{ color: 'var(--text-subtle)', height: 14 }}>
        {[0, 30, 60, 80, 100].map((tick) => (
          <span
            key={tick}
            className="absolute -translate-x-1/2"
            style={{ left: `${tick}%` }}
          >
            {tick}
          </span>
        ))}
      </div>
    </div>
  );
}
